import { useMemo } from 'react';

import { DROPDOWN_FETCH_PAGE_SIZE } from '@klareospace/pharos-lib-front-utils';
import type { TemplateFormFieldDef, TemplateFormFieldOption } from '@klareospace/pharos-lib-types';

import { useOrderTypes } from '&/features/settings/order-types';
import { useSitesForSelect } from '&/features/settings/sites/hooks';

// ─── Types ────────────────────────────────────────────────────────────────────

export interface UseDynamicSelectOptionsReturn {
  /** Map of optionsSource → resolved options, ready to pass as `dynamicOptions` to DynamicFormField. */
  dynamicOptions: Record<string, TemplateFormFieldOption[]>;
  /** True while any of the required data sources are still loading. */
  isLoading: boolean;
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

/** Collects unique optionsSource values used across all dynamic-select fields. */
function collectOptionsSources(fields: TemplateFormFieldDef[]): Set<'order-types' | 'sites'> {
  const sources = new Set<'order-types' | 'sites'>();
  for (const field of fields) {
    if (field.type === 'dynamic-select' && field.optionsSource !== undefined) {
      sources.add(field.optionsSource);
    }
  }
  return sources;
}

// ─── Hook ─────────────────────────────────────────────────────────────────────

/**
 * Resolves remote options for all `dynamic-select` fields in a template's
 * formFields. Each distinct `optionsSource` value triggers exactly one query —
 * queries for unused sources are kept disabled.
 *
 * The returned `dynamicOptions` map is keyed by `optionsSource` and is stable
 * across renders when the underlying data has not changed.
 *
 * `isLoading` is true while ANY required source is still fetching, allowing
 * callers to gate form mount until options are available (no disabled-then-
 * enabled flash on dynamic-select fields).
 *
 * Available sources:
 *   - 'sites'       — fetched via useSitesForSelect (GET /sites/search, page 1, 100 items)
 *   - 'order-types' — fetched via useOrderTypes (POST /order-types/search, active statuses)
 */
export function useDynamicSelectOptions(
  formFields: TemplateFormFieldDef[],
  enabled: boolean,
): UseDynamicSelectOptionsReturn {
  const sources = useMemo(() => collectOptionsSources(formFields), [formFields]);

  const needsSites = sources.has('sites');
  const needsOrderTypes = sources.has('order-types');

  // Sites — delegate to the canonical hook which owns the { label, value } mapping
  // and also exposes the loading flag needed for form-mount gating.
  const { options: siteOptions, isLoading: isLoadingSites } = useSitesForSelect({
    enabled: enabled && needsSites,
  });

  // Order types — no dedicated "for-select" hook exists yet; derive options from useOrderTypes.
  // Maps each OrderType to { label: label, value: code } — the code is the identifier stored
  // in the template configuration value, not the MongoDB id.
  const { data: orderTypesData, isLoading: isLoadingOrderTypes } = useOrderTypes({
    enabled: enabled && needsOrderTypes,
    page: 1,
    pageSize: DROPDOWN_FETCH_PAGE_SIZE,
  });

  const orderTypeOptions: TemplateFormFieldOption[] = useMemo(
    () =>
      (orderTypesData?.items ?? []).map((ot) => ({
        label: ot.label,
        value: ot.code,
      })),
    [orderTypesData],
  );

  const dynamicOptions: Record<string, TemplateFormFieldOption[]> = useMemo(() => {
    const map: Record<string, TemplateFormFieldOption[]> = {};
    if (needsSites) {
      map['sites'] = siteOptions;
    }
    if (needsOrderTypes) {
      map['order-types'] = orderTypeOptions;
    }
    return map;
  }, [needsSites, needsOrderTypes, siteOptions, orderTypeOptions]);

  // Both sources now expose their loading flags so callers can wait for all
  // options before mounting the form.
  const isLoading = (needsSites && isLoadingSites) || (needsOrderTypes && isLoadingOrderTypes);

  return { dynamicOptions, isLoading };
}
